import { useEffect, useState } from "react";
import api from "../lib/api";
import { X, Search, Send, Swords, Users, Loader2, CheckCircle2, Copy, Share2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * LiveInviteModal — opens over the live stream WITHOUT unmounting the parent.
 * - Shows a user picker (search + suggestions)
 * - Sends DM + notification invites via /live/{id}/invite
 * - Also offers external share via native share / copy link
 *
 * Props:
 *  - streamId (string)
 *  - streamTitle (string)
 *  - kind ("invite" | "battle")
 *  - onClose()
 */
export default function LiveInviteModal({ streamId, streamTitle = "Live", kind = "invite", onClose }) {
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState({}); // { userId: true }
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [err, setErr] = useState("");
  const [duration, setDuration] = useState(3); // battle length in minutes (battle invites only)

  const shareUrl = `${window.location.origin}/live/watch/${streamId}`;
  const shareText = `🔴 LIVE NOW: ${streamTitle} — Join me on Hifireels!`;

  // Load default suggestions (recent users) on open
  useEffect(() => {
    const q = query.trim();
    setLoading(true);
    const url = q ? `/users/search?q=${encodeURIComponent(q)}&limit=25` : `/users/search?q=a&limit=25`;
    const t = setTimeout(() => {
      api.get(url).then(r => setUsers(r.data || [])).catch(() => setUsers([])).finally(() => setLoading(false));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [query]);

  const toggle = (uid) => setSelected(s => (kind === "battle" ? { [uid]: !s[uid] } : { ...s, [uid]: !s[uid] }));
  const [pendingBattleId, setPendingBattleId] = useState(null);
  const cancelInvite = async () => {
    const id = pendingBattleId;
    if (!id) return;
    try { await api.post(`/battles/${id}/cancel`); setPendingBattleId(null); setSentCount(0); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not cancel the invite"); }
  };
  const selectedIds = Object.keys(selected).filter(k => selected[k]);

  const send = async () => {
    setErr("");
    if (selectedIds.length === 0) { setErr("Pick at least one user"); return; }
    setSending(true);
    try {
      const { data } = kind === "battle"
        ? await api.post(`/live/${streamId}/battle/invite`, {
            user_ids: selectedIds,
            duration_min: duration,
            message: note,
          })
        : await api.post(`/live/${streamId}/invite`, {
            stream_id: streamId,
            user_ids: selectedIds,
            kind,
            message: note,
          });
      setSentCount(data.sent || 0);
      if (kind === "battle") setPendingBattleId(data.battle_id || null);
      setSelected({});
      setNote("");
      if (kind !== "battle") setTimeout(() => setSentCount(0), 3000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to send invites");
    } finally { setSending(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setSentCount(-1); setTimeout(() => setSentCount(0), 1500); } catch { /* noop */ }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: streamTitle, text: shareText, url: shareUrl }); } catch { /* noop */ }
    } else {
      copyLink();
    }
  };

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
        <div className="absolute inset-0 bg-black/70" onClick={onClose} data-testid="invite-backdrop"/>
        <motion.div initial={{y:"100%"}} animate={{y:0}} exit={{y:"100%"}} transition={{type:"spring", damping:28}}
          className="relative w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl bg-[#0B0F19] border border-white/15 max-h-[85vh] flex flex-col"
          data-testid="live-invite-modal">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              {kind === "battle" ? <Swords className="w-5 h-5 text-[#FF7A00]"/> : <Users className="w-5 h-5 text-[#00E5FF]"/>}
              <h3 className="font-outfit font-extrabold text-lg">
                {kind === "battle" ? "Invite to Battle" : "Invite to Live"}
              </h3>
            </div>
            <button onClick={onClose} data-testid="invite-close" className="p-1.5 rounded-full bg-white/8"><X className="w-4 h-4"/></button>
          </div>

          {/* Quick share row */}
          <div className="px-4 py-2 flex items-center gap-2 border-b border-white/10">
            <button onClick={nativeShare} data-testid="invite-share-native"
              className="flex-1 py-2 rounded-full bg-[#A855F7]/15 border border-[#A855F7]/40 text-[#A855F7] text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95">
              <Share2 className="w-3.5 h-3.5"/>Share stream link
            </button>
            <button onClick={copyLink} data-testid="invite-copy"
              className="py-2 px-3 rounded-full bg-white/8 border border-white/10 text-xs font-bold flex items-center gap-1.5 active:scale-95">
              <Copy className="w-3.5 h-3.5"/>Copy
            </button>
          </div>

          {/* Battle duration (battle invites only) */}
          {kind === "battle" && (
            <div className="px-4 pt-3" data-testid="battle-duration">
              <div className="text-xs text-white/60 mb-1.5">Battle duration</div>
              <div className="flex flex-wrap gap-1.5">
                {[2, 3, 5, 10, 20, 30].map(m => (
                  <button key={m} type="button" onClick={() => setDuration(m)} data-testid={`battle-duration-${m}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold border ${duration === m ? "bg-[#FF7A00]/20 border-[#FF7A00] text-[#FF7A00]" : "bg-white/5 border-white/10"}`}>
                    {m} min
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search */}
          <div className="px-4 py-3">
            <div className="relative">
              <Search className="w-4 h-4 text-white/50 absolute left-3 top-1/2 -translate-y-1/2"/>
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search users by @username or name…"
                data-testid="invite-search"
                className="w-full pl-9 pr-3 py-2.5 rounded-full bg-white/5 border border-white/10 outline-none text-sm focus:border-[#00E5FF]"/>
            </div>
          </div>

          {/* User list */}
          <div className="flex-1 overflow-y-auto px-2 pb-2" data-testid="invite-user-list">
            {loading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#00E5FF]"/></div>
            ) : users.length === 0 ? (
              <div className="text-center py-6 text-white/50 text-sm">No users found</div>
            ) : (
              users.map(u => {
                const on = !!selected[u.id];
                return (
                  <button key={u.id} onClick={() => toggle(u.id)} data-testid={`invite-user-${u.id}`}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-2xl active:scale-[0.99] ${on ? "bg-[#00E5FF]/10 border border-[#00E5FF]/40" : "hover:bg-white/5 border border-transparent"}`}>
                    <img src={u.avatar} alt="" className="w-9 h-9 rounded-full object-cover border border-white/10"/>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-sm font-bold truncate">{u.name || u.username}</div>
                      <div className="text-[10px] text-white/50 truncate">@{u.username} · {u.followers || 0} followers</div>
                    </div>
                    {on && <CheckCircle2 className="w-5 h-5 text-[#00E5FF] flex-shrink-0"/>}
                  </button>
                );
              })
            )}
          </div>

          {/* Note + Send */}
          <div className="p-3 border-t border-white/10 space-y-2">
            {sentCount > 0 && (
              <div data-testid="invite-sent-ok" className="text-emerald-400 text-xs text-center">{kind === "battle" ? `✓ Battle invite sent · waiting for them to accept` : `✓ Sent invite DM to ${sentCount} user${sentCount > 1 ? "s" : ""}`}</div>
            )}
            {kind === "battle" && pendingBattleId && (
              <button onClick={cancelInvite} data-testid="invite-cancel-btn" className="w-full py-2 rounded-full bg-[#FF2A5F]/20 border border-[#FF2A5F]/50 text-[#FF2A5F] text-sm font-bold active:scale-95">Cancel invite</button>
            )}
            {sentCount === -1 && (
              <div className="text-emerald-400 text-xs text-center">✓ Link copied</div>
            )}
            {err && <div data-testid="invite-err" className="text-[#FF2A5F] text-xs text-center">{err}</div>}
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="Personal message (optional)…"
              data-testid="invite-note"
              className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm placeholder:text-white/40"
              maxLength={180}/>
            <button onClick={send} disabled={sending || selectedIds.length === 0} data-testid="invite-send-btn"
              className="w-full py-2.5 rounded-full bg-gradient-to-r from-[#FF2A5F] to-[#00E5FF] text-black font-outfit font-extrabold flex items-center justify-center gap-1.5 disabled:opacity-40 active:scale-95">
              {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
              Send {kind === "battle" ? "Battle" : "Live"} Invite ({selectedIds.length})
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
